import {
  createVanilla,
  isFunction,
  shallowEqual,
  snapshot,
  subscribe
} from "./chunk-22VVHX5B.js";

// src/react/use-snapshot.ts
import { useCallback, useEffect, useRef } from "react";
import { useSyncExternalStoreWithSelector } from "use-sync-external-store/shim/with-selector.js";
function useSnapshot(proxyState, selectorOrOption, maybeOptions) {
  const isUnmountedRef = useRef(false);
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);
  let options;
  let selector;
  if (selectorOrOption && !isFunction(selectorOrOption)) {
    options = selectorOrOption;
    selector = void 0;
  } else {
    options = maybeOptions;
    selector = selectorOrOption;
  }
  const { sync: updateInSync, isEqual = shallowEqual } = options ?? {};
  const _subscribe = useCallback(
    (callback) => subscribe(proxyState, callback, updateInSync),
    [proxyState, updateInSync]
  );
  const _getSnapshot = useCallback(() => snapshot(proxyState), [proxyState]);
  const _selector = selector || ((s) => s);
  const _snapshot = useSyncExternalStoreWithSelector(
    _subscribe,
    _getSnapshot,
    _getSnapshot,
    _selector,
    (a, b) => {
      if (isUnmountedRef.current) return true;
      return isEqual(a, b);
    }
  );
  return _snapshot;
}

// src/enhancers/react/with-use-snapshot.ts
function withUseSnapshot(store) {
  const boundUseSnapshot = (selectorOrOption, maybeOptions) => {
    let options;
    let selector;
    if (selectorOrOption && !isFunction(selectorOrOption)) {
      options = selectorOrOption;
      selector = void 0;
    } else {
      options = maybeOptions;
      selector = selectorOrOption;
    }
    return useSnapshot(store.mutate, selector, options);
  };
  return {
    ...store,
    useSnapshot: boundUseSnapshot
  };
}

// src/react/use-subscribe.ts
import { useEffect as useEffect2, useRef as useRef2 } from "react";
function useSubscribe(proxyState, listener, notifyInSync) {
  const listenerRef = useRef2(listener);
  listenerRef.current = listener;
  useEffect2(() => {
    const unsubscribe = subscribe(
      proxyState,
      (...args) => listenerRef.current(...args),
      notifyInSync
    );
    return unsubscribe;
  }, [proxyState, notifyInSync]);
}

// src/enhancers/react/with-use-subscribe.ts
function withUseSubscribe(store) {
  const boundUseSubscribe = (listener, notifyInSync) => {
    return useSubscribe(store.mutate, listener, notifyInSync);
  };
  return {
    ...store,
    useSubscribe: boundUseSubscribe
  };
}

// src/react/create-with-hooks.ts
function createWithHooks(initialState, options = {}) {
  return withUseSnapshot(withUseSubscribe(createVanilla(initialState, options)));
}

export {
  useSnapshot,
  withUseSnapshot,
  useSubscribe,
  withUseSubscribe,
  createWithHooks
};
